import { Box, Button, Switch, Typography, Divider } from "@mui/material";
import React from "react";
import SongRow, { TRACK_GRID, ADDED_BY_CELL } from "./SongRow";
import PreviewSongRow from "./PreviewSongRow";
import ManualRemovalDialog from "./ManualRemovalDialog";
import PlaylistFollowersDialog from "./PlaylistFollowersDialog";
import Track from "../../models/Track";
import DefaultButton from "../general/buttons/DefaultButton";
import PlaylistCover from "../general/PlaylistCover";
import LinkButton, { MusicProvider } from "./LinkButton";
import {
    fetchSyncPreview,
    commitSync,
    fetchSongStates,
    setSongState,
    SyncPreviewDTO,
    SyncSongDTO,
    SongSyncState,
    notifySyncUsageChanged,
} from "../../services/sync";
import {
    fetchPlaylistDetails,
    checkPlaylistLinked,
    detailRowToTrack,
    requestContributor,
    setPlaylistPublic,
    setPlaylistFollow,
    fetchInviteToken,
    inviteUrlFor,
    notifyPlaylistsChanged,
} from "../../services/playlist";
import { colors, heroGradient } from "../../theme";

const PROVIDERS: MusicProvider[] = ["Spotify", "Apple Music"];

interface PlaylistViewProps {
    playlistDetails: any;
    setPlaylistDetails: (details: any) => void;
}

function toggleSelected(current: Set<number>, setSelected: (next: Set<number>) => void, id: number) {
    const next = new Set(current);
    if (next.has(id)) {
        next.delete(id);
    } else {
        next.add(id);
    }
    setSelected(next);
}

/** Small text link used for the select-all / deselect-all pair. */
function MiniAction({ label, onClick }: { label: string; onClick: () => void }) {
    return (
        <Button
            onClick={onClick}
            sx={{
                p: 0,
                minWidth: 0,
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: colors.textSecondary,
                "&:hover": { color: colors.textPrimary, transform: "none", backgroundColor: "transparent" },
            }}
        >
            {label}
        </Button>
    );
}

function SectionHeader(props: {
    title: string;
    count: number;
    onSelectAll: () => void;
    onDeselectAll: () => void;
}) {
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                mb: 1.5,
                flexWrap: "wrap",
            }}
        >
            <Typography variant="h5">
                {props.title}{" "}
                <Box component="span" sx={{ color: colors.textMuted, fontWeight: 600 }}>
                    ({props.count})
                </Box>
            </Typography>
            {props.count > 0 && (
                <Box sx={{ display: "flex", gap: 2 }}>
                    <MiniAction label="Select all" onClick={props.onSelectAll} />
                    <MiniAction label="Deselect all" onClick={props.onDeselectAll} />
                </Box>
            )}
        </Box>
    );
}

/**
 * Which provider(s) this Shufl playlist is linked to, shown at a glance before
 * picking one - distinct from Settings' "is your account connected" indicator.
 */
function ProviderTabs(props: {
    selected: MusicProvider;
    linkedStatus: Record<MusicProvider, boolean>;
    onSelect: (provider: MusicProvider) => void;
}) {
    return (
        <Box sx={{ display: "flex", gap: 1 }}>
            {PROVIDERS.map((provider) => {
                const isSelected = provider === props.selected;
                const isLinked = props.linkedStatus[provider];
                return (
                    <Button
                        key={provider}
                        onClick={() => props.onSelect(provider)}
                        sx={{
                            px: 1.75,
                            py: 0.75,
                            minWidth: 0,
                            borderRadius: 500,
                            fontSize: 13,
                            fontWeight: 700,
                            textTransform: "none",
                            gap: 0.75,
                            color: isSelected ? "#04121C" : "rgba(255,255,255,0.85)",
                            backgroundColor: isSelected ? colors.accent : "rgba(255,255,255,0.1)",
                            "&:hover": {
                                backgroundColor: isSelected ? colors.accentHover : "rgba(255,255,255,0.18)",
                                transform: "none",
                            },
                        }}
                    >
                        <Box
                            component="span"
                            sx={{
                                width: 7,
                                height: 7,
                                borderRadius: "50%",
                                backgroundColor: isLinked ? colors.success : "rgba(255,255,255,0.4)",
                                flexShrink: 0,
                            }}
                        />
                        {provider}
                    </Button>
                );
            })}
        </Box>
    );
}

export default function PlaylistView(props: PlaylistViewProps) {
    const { playlistDetails, setPlaylistDetails } = props;
    const tracks: Track[] = playlistDetails?.tracks ?? [];
    const hasPlaylist = playlistDetails != null && Object.keys(playlistDetails).length > 0;

    const [syncing, setSyncing] = React.useState(false);
    const [committing, setCommitting] = React.useState(false);
    const [syncPreview, setSyncPreview] = React.useState<SyncPreviewDTO | null>(null);
    const [selectedAdd, setSelectedAdd] = React.useState<Set<number>>(new Set());
    const [selectedRemove, setSelectedRemove] = React.useState<Set<number>>(new Set());
    const [selectedIncoming, setSelectedIncoming] = React.useState<Set<number>>(new Set());
    const [selectedDeletions, setSelectedDeletions] = React.useState<Set<number>>(new Set());
    const [songStates, setSongStates] = React.useState<Record<string, SongSyncState>>({});
    const [manualRemovals, setManualRemovals] = React.useState<SyncSongDTO[]>([]);
    const [syncError, setSyncError] = React.useState<string | null>(null);
    const [followersOpen, setFollowersOpen] = React.useState(false);
    const [requestingContributor, setRequestingContributor] = React.useState(false);
    const [inviteUrl, setInviteUrl] = React.useState<string | null>(null);
    const [inviteCopied, setInviteCopied] = React.useState(false);
    const [selectedProvider, setSelectedProvider] = React.useState<MusicProvider>("Spotify");
    const [linkedStatus, setLinkedStatus] = React.useState<Record<MusicProvider, boolean>>({
        "Spotify": false,
        "Apple Music": false,
    });

    // An invite link belongs to one playlist - carrying it across a selection change
    // would offer the wrong playlist's link.
    React.useEffect(() => {
        setInviteUrl(null);
        setInviteCopied(false);
    }, [playlistDetails?.playlistId]);

    // Re-check both providers whenever the selected playlist changes, and default
    // the tab to whichever is actually linked rather than always landing on Spotify.
    React.useEffect(() => {
        const playlistId = playlistDetails?.playlistId;
        if (!playlistId) return;
        let cancelled = false;

        Promise.all(PROVIDERS.map(provider => checkPlaylistLinked(playlistId, provider)))
            .then(results => {
                if (cancelled) return;
                const status = Object.fromEntries(PROVIDERS.map((p, i) => [p, results[i]])) as Record<MusicProvider, boolean>;
                setLinkedStatus(status);
                if (!status[selectedProvider]) {
                    const firstLinked = PROVIDERS.find(p => status[p]);
                    if (firstLinked) setSelectedProvider(firstLinked);
                }
            });

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [playlistDetails?.playlistId]);

    // Per-song state is per (user, provider), so it reloads when either changes.
    // Requires a link to exist - without one there is nowhere to store state.
    React.useEffect(() => {
        const playlistId = playlistDetails?.playlistId;
        if (!playlistId || !linkedStatus[selectedProvider]) {
            setSongStates({});
            return;
        }
        let cancelled = false;
        fetchSongStates(playlistId, selectedProvider)
            .then(states => { if (!cancelled) setSongStates(states || {}); })
            .catch(() => { if (!cancelled) setSongStates({}); });
        return () => { cancelled = true; };
    }, [playlistDetails?.playlistId, selectedProvider, linkedStatus]);

    const handleStateChange = async (track: Track, state: SongSyncState) => {
        const playlistId = playlistDetails?.playlistId;
        if (!playlistId || !track.songIdentityId) return;
        const identityId = track.songIdentityId;
        const previous = songStates[identityId];

        setSongStates(prev => ({ ...prev, [identityId]: state }));
        try {
            await setSongState(playlistId, selectedProvider, identityId, state);
        } catch (error: any) {
            // Put the old value back rather than leaving the dropdown showing a
            // state the server never accepted.
            setSongStates(prev => {
                const next = { ...prev };
                if (previous === undefined) {
                    delete next[identityId];
                } else {
                    next[identityId] = previous;
                }
                return next;
            });
            setSyncError(error?.message || "Couldn't update that song's status");
        }
    };

    const handleSync = async () => {
        if (!playlistDetails?.playlistId) return;
        setSyncing(true);
        setSyncError(null);
        try {
            const preview = await fetchSyncPreview(playlistDetails.playlistId, selectedProvider);
            setSyncPreview(preview);
            // Pre-checked and deselectable, per the MVP sync definition. Deletions are
            // the exception: they drop a song for every collaborator, so they start
            // unticked and have to be chosen deliberately.
            setSelectedAdd(new Set(preview.toAdd.map(song => song.id)));
            setSelectedRemove(new Set(preview.toRemove.map(song => song.id)));
            setSelectedIncoming(new Set(preview.incoming.map(song => song.id)));
            setSelectedDeletions(new Set());
        } catch (error: any) {
            console.error('Failed to load sync preview:', error);
            setSyncError(error?.message || 'Failed to load sync preview');
        } finally {
            setSyncing(false);
        }
    };

    const handleCancelSync = () => {
        setSyncPreview(null);
        setSelectedAdd(new Set());
        setSelectedRemove(new Set());
        setSelectedIncoming(new Set());
        setSelectedDeletions(new Set());
    };

    const handleConfirmSync = async () => {
        if (!syncPreview || !playlistDetails?.playlistId) return;
        setCommitting(true);
        setSyncError(null);
        try {
            const result = await commitSync(
                playlistDetails.playlistId,
                selectedProvider,
                Array.from(selectedAdd),
                Array.from(selectedRemove),
                Array.from(selectedIncoming),
                Array.from(selectedDeletions)
            );
            // A committed sync spends one of the day's allowance, so the header's
            // counter is now stale.
            notifySyncUsageChanged();
            if (result.notFound && result.notFound.length > 0) {
                setSyncError(`Couldn't match on ${selectedProvider}: ${result.notFound.join(', ')}`);
            }
            if (result.manualRemovals && result.manualRemovals.length > 0) {
                // Dedicated dialog rather than folding into syncError - a file is about
                // to download unprompted, and that needs its own explanation, not a
                // one-line banner easy to miss.
                setManualRemovals(result.manualRemovals);
            }
            handleCancelSync();

            const refreshedTracks = await fetchPlaylistDetails(playlistDetails.playlistId);
            const safeTracks = Array.isArray(refreshedTracks) ? refreshedTracks : [];
            setPlaylistDetails({
                ...playlistDetails,
                tracks: safeTracks.map(detailRowToTrack),
            });

            // Importing or deleting changes what state each song is in, so re-read
            // rather than leaving the dropdowns showing pre-commit values.
            if (linkedStatus[selectedProvider]) {
                fetchSongStates(playlistDetails.playlistId, selectedProvider)
                    .then(states => setSongStates(states || {}))
                    .catch(() => { /* dropdowns fall back to PENDING_ADD */ });
            }
        } catch (error: any) {
            console.error('Sync commit failed:', error);
            setSyncError(error?.message || 'Sync commit failed');
        } finally {
            setCommitting(false);
        }
    };

    const handleTogglePublic = async (next: boolean) => {
        if (!playlistDetails?.playlistId) return;
        setSyncError(null);
        try {
            const isPublic = await setPlaylistPublic(playlistDetails.playlistId, next);
            setPlaylistDetails({ ...playlistDetails, isPublic });
            // Going private takes it off other people's profiles, which the sidebar
            // renders for anyone still following it.
            notifyPlaylistsChanged();
            if (isPublic) {
                setInviteUrl(null);
            }
        } catch (error: any) {
            setSyncError(error?.message || "Couldn't change who can see this playlist");
        }
    };

    const handleCopyInvite = async () => {
        if (!playlistDetails?.playlistId) return;
        setSyncError(null);
        try {
            const token = await fetchInviteToken(playlistDetails.playlistId);
            const url = inviteUrlFor(token);
            setInviteUrl(url);
            // Clipboard access can be refused; the link is shown either way so it can
            // still be copied by hand.
            try {
                await navigator.clipboard.writeText(url);
                setInviteCopied(true);
            } catch {
                setInviteCopied(false);
            }
        } catch (error: any) {
            setSyncError(error?.message || "Couldn't create an invite link");
        }
    };

    const handleUnfollow = async () => {
        if (!playlistDetails?.playlistId) return;
        setSyncError(null);
        try {
            await setPlaylistFollow(playlistDetails.playlistId, false);
            notifyPlaylistsChanged();
            // It's gone from the library, so there's nothing left to show here.
            setPlaylistDetails({});
        } catch (error: any) {
            setSyncError(error?.message || "Couldn't unfollow");
        }
    };

    const handleRequestContributor = async () => {
        if (!playlistDetails?.playlistId) return;
        setRequestingContributor(true);
        setSyncError(null);
        try {
            await requestContributor(playlistDetails.playlistId);
            // Flip to "pending" off the successful call rather than guessing up front -
            // the owner may already have promoted this user from their own screen.
            setPlaylistDetails({ ...playlistDetails, contributorRequested: true });
        } catch (error: any) {
            setSyncError(error?.message || "Couldn't send the request");
        } finally {
            setRequestingContributor(false);
        }
    };

    return (
        <Box
            component="main"
            sx={{
                flexGrow: 1,
                minWidth: 0,
                borderRadius: 2,
                backgroundColor: colors.base,
                overflowY: "auto",
                overflowX: "hidden",
            }}
        >
            {!hasPlaylist ? (
                <Box
                    sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                        px: 3,
                    }}
                >
                    <Typography variant="h3" sx={{ mb: 1.5 }}>Select a playlist</Typography>
                    <Typography variant="body1" sx={{ color: colors.textSecondary, maxWidth: 420 }}>
                        Pick one from your library to see its tracks, link it to Spotify or Apple Music, and sync.
                    </Typography>
                </Box>
            ) : (
                <>
                    {/* ---- Hero ---- */}
                    <Box
                        sx={{
                            background: heroGradient,
                            px: { xs: 3, md: 4 },
                            pt: { xs: 4, md: 6 },
                            pb: 3,
                            display: "flex",
                            alignItems: "flex-end",
                            gap: { xs: 2.5, md: 4 },
                            flexWrap: "wrap",
                        }}
                    >
                        <PlaylistCover
                            images={tracks.map(t => t.imageUrl)}
                            size={{ xs: 140, md: 200 }}
                            borderRadius={2}
                            sx={{ boxShadow: "0 20px 50px rgba(0,0,0,0.6)" }}
                        />
                        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                            <Typography
                                sx={{
                                    fontSize: 12,
                                    fontWeight: 800,
                                    letterSpacing: "0.14em",
                                    textTransform: "uppercase",
                                    color: "rgba(255,255,255,0.78)",
                                    mb: 1,
                                }}
                            >
                                Shared playlist
                            </Typography>
                            <Typography variant="h1" sx={{ fontSize: { xs: "2.25rem", md: "4rem" }, mb: 1.5 }}>
                                {playlistDetails.playlistName}
                            </Typography>
                            <Typography sx={{ color: "rgba(255,255,255,0.75)", fontSize: 14, mb: 2 }}>
                                {tracks.length} {tracks.length === 1 ? "song" : "songs"}
                                {playlistDetails.owned && (playlistDetails.isPublic ? " · Public" : " · Private")}
                            </Typography>

                            {playlistDetails.owned && (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, flexWrap: "wrap" }}>
                                    <Switch
                                        checked={!!playlistDetails.isPublic}
                                        onChange={(e) => handleTogglePublic(e.target.checked)}
                                        inputProps={{ "aria-label": "Public playlist" }}
                                    />
                                    <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.75)" }}>
                                        {playlistDetails.isPublic
                                            ? "Anyone can find this on your profile"
                                            : "Hidden - shareable only by link"}
                                    </Typography>
                                    {!playlistDetails.isPublic && (
                                        <DefaultButton
                                            text={inviteUrl ? (inviteCopied ? "Link copied" : "Show link again") : "Copy invite link"}
                                            variant="secondary"
                                            size="small"
                                            onClick={handleCopyInvite}
                                        />
                                    )}
                                </Box>
                            )}

                            {inviteUrl && (
                                <Typography
                                    sx={{
                                        fontSize: 12,
                                        color: "rgba(255,255,255,0.75)",
                                        mb: 2,
                                        wordBreak: "break-all",
                                        userSelect: "all",
                                    }}
                                >
                                    {inviteUrl}
                                </Typography>
                            )}

                            <Box sx={{ mb: 2 }}>
                                <ProviderTabs
                                    selected={selectedProvider}
                                    linkedStatus={linkedStatus}
                                    onSelect={(provider) => {
                                        // A preview in progress belongs to whichever provider was
                                        // selected when Sync was clicked - switching tabs mid-preview
                                        // would otherwise leave it on screen against the wrong one.
                                        if (syncPreview) handleCancelSync();
                                        setSelectedProvider(provider);
                                    }}
                                />
                            </Box>

                            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                                <DefaultButton
                                    text={syncing ? "Loading…" : "Sync"}
                                    size="large"
                                    onClick={handleSync}
                                    disabled={syncing || !!syncPreview}
                                />
                                <LinkButton
                                    playlistId={playlistDetails.playlistId}
                                    provider={selectedProvider}
                                    onLinkedChange={(linked) => setLinkedStatus(prev => ({ ...prev, [selectedProvider]: linked }))}
                                />
                                {playlistDetails.owned && (
                                    <DefaultButton
                                        text="Manage contributors"
                                        variant="secondary"
                                        size="large"
                                        onClick={() => setFollowersOpen(true)}
                                    />
                                )}
                                {!playlistDetails.owned && playlistDetails.following && (
                                    <DefaultButton
                                        text="Unfollow"
                                        variant="secondary"
                                        size="large"
                                        onClick={handleUnfollow}
                                    />
                                )}
                                {!playlistDetails.owned && playlistDetails.contributor && (
                                    <Typography sx={{ fontSize: 14, color: colors.textSecondary }}>
                                        You're a contributor
                                    </Typography>
                                )}
                                {!playlistDetails.owned && !playlistDetails.contributor
                                    && playlistDetails.following && !playlistDetails.contributorRequested && (
                                    <DefaultButton
                                        text={requestingContributor ? "Sending…" : "Request to contribute"}
                                        variant="secondary"
                                        size="large"
                                        disabled={requestingContributor}
                                        onClick={handleRequestContributor}
                                    />
                                )}
                                {!playlistDetails.owned && !playlistDetails.contributor
                                    && playlistDetails.contributorRequested && (
                                    <Typography sx={{ fontSize: 14, color: colors.textSecondary }}>
                                        Request pending
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    </Box>

                    {/* ---- Body ---- */}
                    <Box sx={{ px: { xs: 2, md: 3 }, py: 3 }}>
                        {syncError && (
                            <Box
                                sx={{
                                    mb: 3,
                                    px: 2,
                                    py: 1.5,
                                    borderRadius: 1.5,
                                    backgroundColor: "rgba(242,85,90,0.12)",
                                    border: `1px solid rgba(242,85,90,0.35)`,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: 2,
                                }}
                            >
                                <Typography sx={{ color: colors.danger, fontSize: 14 }}>{syncError}</Typography>
                                <MiniAction label="Dismiss" onClick={() => setSyncError(null)} />
                            </Box>
                        )}

                        {syncPreview ? (
                            <>
                                <Box sx={{ mb: 4 }}>
                                    <SectionHeader
                                        title="Songs to add"
                                        count={syncPreview.toAdd.length}
                                        onSelectAll={() => setSelectedAdd(new Set(syncPreview.toAdd.map(s => s.id)))}
                                        onDeselectAll={() => setSelectedAdd(new Set())}
                                    />
                                    {syncPreview.toAdd.length === 0 ? (
                                        <Typography variant="body2">Nothing new to add.</Typography>
                                    ) : (
                                        syncPreview.toAdd.map((song, i) => (
                                            <PreviewSongRow
                                                key={`add-${song.id}`}
                                                index={i}
                                                song={song}
                                                checked={selectedAdd.has(song.id)}
                                                onToggle={() => toggleSelected(selectedAdd, setSelectedAdd, song.id)}
                                            />
                                        ))
                                    )}
                                </Box>

                                <Divider sx={{ mb: 4 }} />

                                <Box sx={{ mb: 4 }}>
                                    <SectionHeader
                                        title={`New on ${selectedProvider}`}
                                        count={syncPreview.incoming.length}
                                        onSelectAll={() => setSelectedIncoming(new Set(syncPreview.incoming.map(s => s.id)))}
                                        onDeselectAll={() => setSelectedIncoming(new Set())}
                                    />
                                    {syncPreview.incoming.length > 0 && (
                                        <Typography sx={{ color: colors.textMuted, fontSize: 13, mb: 1.5, fontStyle: "italic" }}>
                                            These are in your {selectedProvider} playlist but not in this shared
                                            playlist yet. Adding them makes them visible to everyone on it.
                                        </Typography>
                                    )}
                                    {syncPreview.incoming.length === 0 ? (
                                        <Typography variant="body2">Nothing new on {selectedProvider}.</Typography>
                                    ) : (
                                        syncPreview.incoming.map((song, i) => (
                                            <PreviewSongRow
                                                key={`incoming-${song.id}`}
                                                index={i}
                                                song={song}
                                                checked={selectedIncoming.has(song.id)}
                                                onToggle={() => toggleSelected(selectedIncoming, setSelectedIncoming, song.id)}
                                            />
                                        ))
                                    )}
                                </Box>

                                <Divider sx={{ mb: 4 }} />

                                <Box sx={{ mb: 4 }}>
                                    <SectionHeader
                                        title={`Songs removed on ${selectedProvider}`}
                                        count={syncPreview.toRemove.length}
                                        onSelectAll={() => setSelectedRemove(new Set(syncPreview.toRemove.map(s => s.id)))}
                                        onDeselectAll={() => setSelectedRemove(new Set())}
                                    />
                                    {!syncPreview.removalSupported && syncPreview.toRemove.length > 0 && (
                                        <Typography sx={{ color: colors.textMuted, fontSize: 13, mb: 1.5, fontStyle: "italic" }}>
                                            {selectedProvider} doesn't support removing tracks automatically - confirming
                                            will tell you which ones to remove yourself instead of applying it.
                                        </Typography>
                                    )}
                                    {syncPreview.toRemove.length === 0 ? (
                                        <Typography variant="body2">Nothing flagged as removed.</Typography>
                                    ) : (
                                        syncPreview.toRemove.map((song, i) => (
                                            <PreviewSongRow
                                                key={`remove-${song.id}`}
                                                index={i}
                                                song={song}
                                                checked={selectedRemove.has(song.id)}
                                                onToggle={() => toggleSelected(selectedRemove, setSelectedRemove, song.id)}
                                            />
                                        ))
                                    )}
                                </Box>

                                {syncPreview.masterDeletions.length > 0 && (
                                    <>
                                        <Divider sx={{ mb: 4 }} />
                                        <Box sx={{ mb: 4 }}>
                                            <SectionHeader
                                                title="Remove from the shared playlist"
                                                count={syncPreview.masterDeletions.length}
                                                onSelectAll={() => setSelectedDeletions(new Set(syncPreview.masterDeletions.map(s => s.id)))}
                                                onDeselectAll={() => setSelectedDeletions(new Set())}
                                            />
                                            <Typography sx={{ color: colors.danger, fontSize: 13, mb: 1.5 }}>
                                                You added these and have since taken them out of your {selectedProvider}
                                                {" "}playlist. Ticking one removes it from this shared playlist for
                                                everyone, not just you.
                                            </Typography>
                                            {syncPreview.masterDeletions.map((song, i) => (
                                                <PreviewSongRow
                                                    key={`delete-${song.id}`}
                                                    index={i}
                                                    song={song}
                                                    checked={selectedDeletions.has(song.id)}
                                                    onToggle={() => toggleSelected(selectedDeletions, setSelectedDeletions, song.id)}
                                                />
                                            ))}
                                        </Box>
                                    </>
                                )}

                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "flex-end",
                                        gap: 2,
                                        pt: 1,
                                        borderTop: `1px solid ${colors.line}`,
                                    }}
                                >
                                    <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
                                        <DefaultButton text="Cancel" variant="secondary" onClick={handleCancelSync} />
                                        <DefaultButton
                                            text={committing ? "Applying…" : "Confirm sync"}
                                            onClick={handleConfirmSync}
                                            disabled={committing}
                                        />
                                    </Box>
                                </Box>
                            </>
                        ) : (
                            <>
                                {/* Column header, Spotify's track-table style */}
                                <Box
                                    sx={{
                                        display: "grid",
                                        gridTemplateColumns: TRACK_GRID,
                                        gap: 2,
                                        px: 2,
                                        pb: 1,
                                        borderBottom: `1px solid ${colors.line}`,
                                        mb: 1,
                                        fontSize: 12,
                                        fontWeight: 700,
                                        letterSpacing: "0.1em",
                                        textTransform: "uppercase",
                                        color: colors.textMuted,
                                    }}
                                >
                                    <Box sx={{ textAlign: "right" }}>#</Box>
                                    <Box />
                                    <Box>Title</Box>
                                    <Box>Artist</Box>
                                    <Box sx={ADDED_BY_CELL}>Added by</Box>
                                    <Box>Status</Box>
                                </Box>

                                {tracks.length === 0 ? (
                                    <Box sx={{ px: 2, py: 6, textAlign: "center" }}>
                                        <Typography variant="h5" sx={{ mb: 1 }}>No songs yet</Typography>
                                        <Typography variant="body2">
                                            Once tracks are added to this playlist they'll show up here.
                                        </Typography>
                                    </Box>
                                ) : (
                                    tracks.map((track: Track, index: number) => (
                                        <SongRow
                                            key={track.id ?? index}
                                            index={index}
                                            track={{
                                                ...track,
                                                state: track.songIdentityId
                                                    ? songStates[track.songIdentityId]
                                                    : undefined,
                                            }}
                                            onStateChange={handleStateChange}
                                            // Nowhere to store state until the playlist is
                                            // linked to this provider.
                                            stateDisabled={!linkedStatus[selectedProvider]}
                                        />
                                    ))
                                )}
                            </>
                        )}
                    </Box>
                </>
            )}

            <PlaylistFollowersDialog
                open={followersOpen}
                playlistId={playlistDetails?.playlistId}
                playlistName={playlistDetails?.playlistName || "playlist"}
                onClose={() => setFollowersOpen(false)}
            />

            <ManualRemovalDialog
                open={manualRemovals.length > 0}
                provider={selectedProvider}
                playlistName={playlistDetails?.playlistName || "playlist"}
                songs={manualRemovals}
                onClose={() => setManualRemovals([])}
            />
        </Box>
    );
}
